import '@explorins/pers-shared';
import { b as buildPaginationParams, n as normalizeToPaginated } from './pagination-utils-9vQ-Npkr.js';

/**
 * Platform-Agnostic Purchase API Client (RESTful Architecture)
 *
 * Handles purchase and payment operations using the PERS backend's new RESTful endpoints.
 * Uses @explorins/pers-shared DTOs for consistency with backend.
 *
 * Migration Status: Updated to match /purchases controller (replaces /purchase endpoints)
 *
 * Available Access Levels:
 * - PUBLIC: Project key authentication for catalog browsing and payment operations
 * - USER: Requires user authentication JWT (purchase creation, history access)
 * - ADMIN: Requires tenant admin privileges (not implemented in this client)
 *
 * Note: This SDK focuses on backend purchase operations only.
 * Payment provider integrations (Stripe, etc.) should remain in infrastructure layer.
 */
class PurchaseApi {
    constructor(apiClient) {
        this.apiClient = apiClient;
        this.basePath = '/purchases';
    }
    // ==========================================
    // PUBLIC OPERATIONS (Project Key)
    // ==========================================
    /**
     * PUBLIC: Get purchase tokens (Intelligent Access)
     *
     * RESTful endpoint: GET /purchases/tokens
     * Replaces: GET /purchase/token
     *
     * INTELLIGENT ACCESS:
     * - PUBLIC (Project Key): Returns active tokens only (active parameter ignored)
     * - ADMIN (Tenant Admin JWT): Returns filtered results based on active parameter
     *
     * @param active Filter by active status
     * @param options Pagination options (page, limit, sortBy, sortOrder)
     * @returns Paginated response with purchase tokens
     */
    async getPurchaseTokens(active, options) {
        const params = buildPaginationParams(options);
        if (active !== undefined) {
            params.append('active', String(active));
        }
        const response = await this.apiClient.get(`${this.basePath}/tokens?${params.toString()}`);
        return normalizeToPaginated(response);
    }
    /**
     * PUBLIC: Get donation types
     *
     * RESTful endpoint: GET /purchases/donation-types
     * Replaces: GET /purchase/donation/type
     *
     * @param options Pagination options (page, limit, sortBy, sortOrder)
     * @returns Paginated response with donation types
     */
    async getDonationTypes(options) {
        const params = buildPaginationParams(options);
        const response = await this.apiClient.get(`${this.basePath}/donation-types?${params.toString()}`);
        return normalizeToPaginated(response);
    }
    // ==========================================
    // PAYMENT OPERATIONS (FINANCIAL - CRITICAL)
    // ==========================================
    /**
     * PUBLIC: Create payment intent (FINANCIAL OPERATION)
     *
     * RESTful endpoint: POST /purchases/payment-intents
     * Replaces: POST /purchase/payment-intent
     *
     * CRITICAL: Handles real money operations - tenant context required
     */
    async createPaymentIntent(amount, currency, receiptEmail, description) {
        const body = {
            amount,
            currency,
            receiptEmail,
            description
        };
        return this.apiClient.post(`${this.basePath}/payment-intents`, body);
    }
    /**
     * PUBLIC: Update payment intent (FINANCIAL OPERATION)
     *
     * RESTful endpoint: PUT /purchases/payment-intents/{paymentIntentId}
     * Replaces: PUT /purchase/payment-intent/{paymentIntentId}
     *
     * CRITICAL: Handles real money operations - tenant context required
     */
    async updatePaymentIntent(paymentIntentId, amount, currency, receiptEmail, description) {
        const body = {
            amount,
            currency,
            receiptEmail,
            description
        };
        return this.apiClient.put(`${this.basePath}/payment-intents/${paymentIntentId}`, body);
    }
    /**
     * PUBLIC: Cancel payment intent (FINANCIAL OPERATION)
     *
     * RESTful endpoint: DELETE /purchases/payment-intents/{paymentIntentId}
     * Replaces: DELETE /purchase/payment-intent/{paymentIntentId}
     *
     * CRITICAL: Handles real money operations - tenant context required
     */
    async cancelPaymentIntent(paymentIntentId) {
        return this.apiClient.delete(`${this.basePath}/payment-intents/${paymentIntentId}`);
    }
    // ==========================================
    // USER OPERATIONS (JWT + Project Key)
    // ==========================================
    /**
     * USER: Create purchase (BUSINESS CRITICAL - FINANCIAL TRANSACTION)
     *
     * RESTful endpoint: POST /purchases
     * Replaces: POST /purchase/auth
     *
     * USER-ONLY: Requires user authentication JWT for purchase creation
     * CRITICAL: Real financial transaction with Stripe integration
     */
    async createUserPurchase(paymentIntentId, amount, purchaseTokenId, donationTypeId, donationAccountAddress) {
        const body = {
            quantity: amount,
            purchaseTokenId: purchaseTokenId || '',
            donationTypeId,
            donationAccountAddress,
            paymentIntentId
        };
        return this.apiClient.post(`${this.basePath}`, body);
    }
    /**
     * USER: Get user purchase history
     *
     * RESTful endpoint: GET /purchases/me/history
     * Replaces: GET /purchase/auth
     *
     * USER-ONLY: Get authenticated user's purchase history
     * FINANCIAL RECORDS: User attribution critical for compliance
     *
     * @param options - Pagination options (page, limit, sortBy, sortOrder)
     * @returns Paginated response with purchase history
     */
    async getUserPurchaseHistory(options) {
        const params = buildPaginationParams(options);
        const response = await this.apiClient.get(`${this.basePath}/me/history?${params.toString()}`);
        return normalizeToPaginated(response);
    }
}

/**
 * Platform-Agnostic Payment Service
 *
 * Contains payment business logic and operations that work across platforms.
 * No framework dependencies - pure TypeScript business logic.
 *
 * Focuses only on actual backend capabilities.
 * Payment provider logic (Stripe, etc.) should remain in infrastructure layer.
 */
class PaymentService {
    constructor(paymentApi) {
        this.paymentApi = paymentApi;
    }
    // ==========================================
    // PUBLIC OPERATIONS
    // ==========================================
    /**
     * PUBLIC: Get active purchase tokens
     */
    async getActivePurchaseTokens(active = true, options) {
        return this.paymentApi.getPurchaseTokens(active, options);
    }
    /**
     * PUBLIC: Create payment intent
     */
    async createPaymentIntent(amount, currency, receiptEmail, description) {
        return this.paymentApi.createPaymentIntent(amount, currency, receiptEmail, description);
    }
    /**
     * PUBLIC: Update payment intent
     */
    async updatePaymentIntent(paymentIntentId, amount, currency, receiptEmail, description) {
        return this.paymentApi.updatePaymentIntent(paymentIntentId, amount, currency, receiptEmail, description);
    }
    /**
     * PUBLIC: Cancel payment intent
     */
    async cancelPaymentIntent(paymentIntentId) {
        return this.paymentApi.cancelPaymentIntent(paymentIntentId);
    }
    // ==========================================
    // AUTHENTICATED OPERATIONS
    // ==========================================
    /**
     * AUTH: Create purchase
     */
    async createPurchase(paymentIntentId, amount, purchaseTokenId, donationTypeId, donationAccountAddress) {
        return this.paymentApi.createUserPurchase(paymentIntentId, amount, purchaseTokenId, donationTypeId, donationAccountAddress);
    }
    /**
     * AUTH: Get all user purchases
     */
    async getAllUserPurchases(options) {
        return this.paymentApi.getUserPurchaseHistory(options);
    }
}

export { PurchaseApi as P, PaymentService as a };
//# sourceMappingURL=payment-service-IvM6rryM.js.map
